import { FC, useCallback } from 'react';
import axios from 'axios';
import { router } from '@inertiajs/react';

import { Button } from '@/Components/Button';
import { useToast } from '@/hook/useToast';
import { AppModalProps } from '@/types/modal.type';
import { ModalView } from '@/Components/ModalView';
import { FlickAnimation } from '@/Components/FlickAnimation';
import { FruitBlueberrySvg } from '@/Components/Icons/FruitBlueberrySvg';

type UnarchiveFlowModalProps = AppModalProps<{
    flowId: number;
    flowName: string;
}>;

export const UnarchiveFlowModal: FC<UnarchiveFlowModalProps> = ({
    payload,
    onClose,
}) => {
    const { showToast } = useToast();

    const onUnarchive = useCallback(() => {
        onClose();

        return new Promise<null>((resolve, reject) => {
            axios
                .post(
                    route('api.internal.flows.unarchive', {
                        flowId: payload.flowId,
                    }),
                )
                .then(() => {
                    showToast({
                        autoClose: 5000,
                        variant: 'success',
                        label: `Flow ${payload.flowName} has been unarchived`,
                    });
                    router.reload();
                    resolve(null);
                })
                .catch((error) => {
                    const message =
                        error?.response?.data?.message ?? 'Failed to unarchive flow.';
                    showToast({
                        autoClose: 5000,
                        variant: 'danger',
                        label: message,
                    });
                    reject(error);
                });
        });
    }, [payload.flowId, payload.flowName, onClose]);

    return (
        <ModalView
            maxWidth="sm"
            onClose={onClose}
            onSubmit={onUnarchive}
            contentContainerClassName="py-6 px-8 text-brand-ocean flex flex-col justify-center items-center">
            <div className="relative min-w-60 h-24 flex items-center justify-center">
                <FlickAnimation className="absolute z-10 bottom-5 left-11" />
                <div className="absolute bottom-5 right-20">
                    <FruitBlueberrySvg />
                </div>
            </div>
            <div className="w-full !font-normal text-heading-five text-left text-gray-80 mb-2">
                Unarchiving this Flow will make it visible in your default views again.
            </div>
            <div className="mt-2 w-full flex items-center justify-center space-x-3">
                <Button
                    className="w-full h-9"
                    variant="primary"
                    onClick={onUnarchive}>
                    Unarchive Flow
                </Button>
                <Button
                    className="w-full h-9"
                    variant="default"
                    onClick={onClose}>
                    Cancel
                </Button>
            </div>
        </ModalView>
    );
};
